import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, Sparkles, Crown, Zap, Download } from "lucide-react";
import { Reveal } from "../components/Reveal";
import Seo from "../components/Seo";
import { api, formatINR } from "../lib/api";

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [yearly, setYearly] = useState(false);

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="pricing-page" className="pt-32 pb-24 min-h-screen relative">
      <Seo title="Pricing — ASCENDXP" description="Choose your rank. ASCENDXP Pro and Elite plans with monthly and yearly billing." />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full bg-indigo-600/10 blur-[120px]" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <Reveal className="text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-indigo-400 font-bold mb-4">Membership</p>
          <h1 className="font-heading font-black text-4xl sm:text-6xl tracking-tighter">CHOOSE YOUR RANK</h1>
          <p className="mt-5 text-zinc-400 max-w-xl mx-auto">
            Unlock the full arena. Choose the plan that fits your ascent — manage everything inside the ASCENDXP app.
          </p>
        </Reveal>

        {/* Toggle */}
        <Reveal delay={0.1}>
          <div className="mt-10 flex items-center justify-center gap-4">
            <span className={`text-sm font-medium ${!yearly ? "text-white" : "text-zinc-500"}`}>Monthly</span>
            <button
              data-testid="pricing-toggle"
              onClick={() => setYearly((v) => !v)}
              className={`relative inline-flex h-8 w-16 items-center rounded-full transition-colors ${yearly ? "bg-indigo-600" : "bg-white/10"}`}
            >
              <motion.span
                layout
                className="inline-block h-6 w-6 rounded-full bg-white shadow"
                animate={{ x: yearly ? 36 : 4 }}
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
              />
            </button>
            <span className={`text-sm font-medium flex items-center gap-2 ${yearly ? "text-white" : "text-zinc-500"}`}>
              Yearly
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#d4af37]/15 text-[#d4af37] font-bold">Save ~17%</span>
            </span>
          </div>
        </Reveal>

        {/* Plan cards */}
        <div className="mt-14 grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {plans.map((p, i) => {
            const price = yearly ? p.yearly_price : p.monthly_price;
            const isElite = p.id === "elite";
            const isPro = p.id === "pro";
            const Icon = isElite ? Crown : isPro ? Zap : Sparkles;
            return (
              <Reveal key={p.id} delay={i * 0.1}>
                <div
                  data-testid={`plan-card-${p.id}`}
                  className={`relative rounded-2xl p-8 h-full flex flex-col ${
                    isElite
                      ? "bg-black border border-[#d4af37]/50 glow-gold"
                      : isPro
                      ? "glass border-indigo-500/40 glow-indigo"
                      : "glass"
                  }`}
                >
                  {p.badge && (
                    <span className={`absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-wider px-3 py-1 rounded-full font-bold ${isElite ? "bg-[#d4af37] text-black" : "bg-indigo-600 text-white"}`}>
                      {p.badge}
                    </span>
                  )}
                  <div className={`w-11 h-11 rounded-xl grid place-items-center mb-5 ${isElite ? "bg-[#d4af37]/15 text-[#d4af37]" : "bg-indigo-600/15 text-indigo-400"}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-heading font-bold text-xl">{p.name}</h3>
                  <p className="text-sm text-zinc-500 mt-1">{p.tagline}</p>

                  <div className="mt-6 flex items-end gap-1">
                    <span className="font-heading font-black text-4xl">{price === 0 ? "Free" : formatINR(price)}</span>
                    {price !== 0 && <span className="text-zinc-500 text-sm mb-1">/{yearly ? "yr" : "mo"}</span>}
                  </div>

                  <ul className="mt-7 space-y-3 flex-1">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-sm text-zinc-300">
                        <Check className={`w-4 h-4 mt-0.5 shrink-0 ${isElite ? "text-[#d4af37]" : "text-indigo-400"}`} />
                        {f}
                      </li>
                    ))}
                  </ul>

                  <a
                    href="#"
                    data-testid={`plan-cta-${p.id}`}
                    className={`mt-8 w-full ${p.id === "free" ? "btn-ghost" : isElite ? "btn-gold" : "btn-primary"}`}
                  >
                    <Download className="w-5 h-5" /> Get the App
                  </a>
                </div>
              </Reveal>
            );
          })}
        </div>

        <p className="text-center text-xs text-zinc-600 mt-10">
          All prices in INR, inclusive of taxes. Plans and subscriptions are managed inside the ASCENDXP mobile app.
        </p>
      </div>
    </div>
  );
}
